// /checkout/trocar?token=... — troca de plano self-service (sem login).
//
// Visual: kit do checkout público (`components/checkout-public/kit.tsx`) —
// fundo creme, título editorial com brushstroke, coluna de escolha + resumo
// sticky à direita. Ter duas caras de checkout no mesmo domínio quebra a
// confiança de quem paga.
//
// O cliente chega logado do SaaS via token assinado.
//   - SUBIR: paga só a diferença proporcional aos DIAS QUE FALTAM do ciclo atual,
//     e o plano troca quando o pagamento confirma (em trial a diferença é zero).
//   - DESCER: não cobra nem devolve; agenda pro fim do ciclo já pago.

import { ArrowRight, Calendar, Check, CreditCard, QrCode, TrendingDown, TrendingUp } from 'lucide-react';
import { useEffect, useLayoutEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

import {
  EloIcon,
  MasterCardIcon,
  PixIcon,
  VisaIcon,
} from '../../components/checkout-public/branded-icons';
import {
  CardInput,
  CheckoutErrorBox,
  CheckoutSection,
  CheckoutSectionTitle,
  CheckoutShell,
  CheckoutWaitingFooter,
  CopyCodeField,
} from '../../components/checkout-public/kit';

import {
  fetchChargeCheckoutStatus,
  payCharge,
  type ChargePayResult,
} from '../../lib/charge-checkout-api';
import {
  cycleSuffix,
  fetchPlanChangePreview,
  keepPlanChangeSubscription,
  startPlanChange,
  type EligibleUpgrade,
  type PlanChangePreview,
} from '../../lib/plan-change-api';
import { planChangeMath } from '../../lib/plan-change-math';
import { readHandoffIssuer } from '../../lib/handoff-issuer';
import { ReserveiPlanChange } from '../../components/checkout-reservei/ReserveiPlanChange';
import {
  normalizeReserveiReturnPath,
  readReserveiCheckoutIntent,
  saveReserveiCheckoutIntent,
} from '../../lib/public-checkout-api';

/** Boleto fora: a troca só vale quando o pagamento confirma, e boleto leva dias. */
type Method = 'pix' | 'credit_card';

const PAYMENT_METHODS: ReadonlyArray<{
  id: Method;
  label: string;
  hint: string;
  badge?: string;
}> = [
  {
    id: 'pix',
    label: 'Pix',
    hint: 'Aprovação imediata · sem taxas adicionais',
    badge: 'Instantâneo',
  },
  {
    id: 'credit_card',
    label: 'Cartão de crédito',
    hint: 'Visa, Mastercard, Elo · cobrança recorrente automática',
  },
];

function formatBRL(v: string): string {
  const n = Number(v);
  return Number.isFinite(n) ? n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) : v;
}

/** 'YYYY-MM-DD' → 'DD/MM'. Sem `new Date` (evita voltar 1 dia por fuso). */
function formatDay(ymd: string | null | undefined): string {
  if (!ymd) return '—';
  const [, m, d] = ymd.split('-');
  return m && d ? `${d}/${m}` : ymd;
}

function plural(n: number, one: string, many: string): string {
  return n === 1 ? one : many;
}

const onlyDigits = (v: string): string => v.replace(/\D/g, '');
const maskCard = (v: string): string =>
  onlyDigits(v)
    .slice(0, 16)
    .replace(/(.{4})/g, '$1 ')
    .trim();
const maskCep = (v: string): string => {
  const d = onlyDigits(v).slice(0, 8);
  return d.length <= 5 ? d : `${d.slice(0, 5)}-${d.slice(5)}`;
};

/** Segundos na tela de "pronto" antes de voltar sozinho pro app do SaaS. */
const AUTO_RETURN_SECONDS = 5;

const EMPTY_CARD = {
  number: '',
  holder_name: '',
  expiry_month: '',
  expiry_year: '',
  ccv: '',
  postal_code: '',
  address_number: '',
};

/** Pagamento em andamento DENTRO desta tela — o cliente não sai pro Asaas. */
interface PendingPayment {
  chargeId: string;
  method: Method;
  /** Só Pix: QR + copia-e-cola. */
  pix: ChargePayResult | null;
  /** Fatura do Asaas — só como saída de emergência se o QR não vier. */
  fallbackUrl: string | null;
}

export function PlanChangePage(): JSX.Element {
  const [params] = useSearchParams();
  const savedIntent = readReserveiCheckoutIntent();
  const queryToken = params.get('token') ?? '';
  const token = queryToken || savedIntent?.handoffToken || '';
  /** Plano que o cliente clicou no SaaS — destino do fallback de compra normal. */
  const wantedPlan = params.get('plan') ?? savedIntent?.planSlug ?? '';
  const saasOrigin = params.get('saas') ?? 'reservei';

  useLayoutEffect(() => {
    if (!queryToken) return;
    saveReserveiCheckoutIntent({
      handoffToken: queryToken,
      planSlug: params.get('plan') ?? undefined,
      cycle: params.get('cycle') ?? undefined,
      redirect: params.get('redirect') ?? undefined,
    });
    const clean = new URL(window.location.href);
    clean.searchParams.delete('token');
    window.history.replaceState(window.history.state, '', `${clean.pathname}${clean.search}${clean.hash}`);
  }, [queryToken, params]);
  const [preview, setPreview] = useState<PlanChangePreview | null>(null);
  /** Retorno aceita caminho relativo do link ou a URL já cadastrada no produto. */
  const reserveiReturn = params.get('saas') === 'reservei' || Boolean(savedIntent);
  const requestedReturn = params.get('redirect');
  const returnPath = reserveiReturn
    ? normalizeReserveiReturnPath(requestedReturn) ?? savedIntent?.redirect
    : undefined;
  let backUrl = '';
  if (!reserveiReturn && requestedReturn && /^https?:\/\//i.test(requestedReturn)) {
    backUrl = requestedReturn;
  } else if (preview?.saas_url) {
    try {
      const configured = new URL(preview.saas_url);
      if (
        (configured.protocol === 'https:' || configured.protocol === 'http:') &&
        !configured.username &&
        !configured.password
      ) {
        backUrl = returnPath ? new URL(returnPath, configured).toString() : configured.toString();
      }
    } catch {
      // Sem origem configurada, não redireciona automaticamente.
    }
  }
  const [loadError, setLoadError] = useState<string | null>(null);
  const [selected, setSelected] = useState<EligibleUpgrade | null>(null);
  const [method, setMethod] = useState<Method | null>(null);
  const [starting, setStarting] = useState(false);
  const [done, setDone] = useState(false);
  /** Preenchido no downgrade: data em que o plano menor passa a valer. */
  const [scheduledFor, setScheduledFor] = useState<string | null>(null);
  /** Assinatura suspensa: `null` = suspensa sem cobrança pagável localizada. */
  const [overdueUrl, setOverdueUrl] = useState<string | null | undefined>(undefined);
  /**
   * Cancelamento agendado (ainda com acesso): `undefined` = não; senão o último
   * dia de acesso. A troca não opera em cima dele — oferece "manter assinatura".
   */
  const [pendingCancellation, setPendingCancellation] = useState<{ endsAt: string | null } | undefined>(
    undefined,
  );
  const [keeping, setKeeping] = useState(false);
  const [keepError, setKeepError] = useState<string | null>(null);
  /** Rebusca a prévia depois do "manter" (a assinatura volta a ser trocável). */
  const [previewRound, setPreviewRound] = useState(0);
  const [card, setCard] = useState(EMPTY_CARD);
  const [payment, setPayment] = useState<PendingPayment | null>(null);
  /** Pagamento confirmado (polling) — muda o texto da tela de pronto. */
  const [paid, setPaid] = useState(false);

  /** Upgrade concluído (pago ou sem cobrança) — volta sozinho pro app. */
  const autoReturn = done && !scheduledFor && Boolean(backUrl);
  const [returnIn, setReturnIn] = useState(AUTO_RETURN_SECONDS);
  useEffect(() => {
    if (!autoReturn) return;
    if (returnIn <= 0) {
      window.location.href = backUrl;
      return;
    }
    const t = window.setTimeout(() => setReturnIn((s) => s - 1), 1_000);
    return () => window.clearTimeout(t);
  }, [autoReturn, returnIn, backUrl]);

  // Acompanha a confirmação do pagamento. O plano troca no backend pelo webhook
  // do Asaas; aqui só esperamos a charge virar paga pra mostrar "pronto".
  useEffect(() => {
    if (!payment || paid) return;
    let alive = true;
    const timer = window.setInterval(() => {
      fetchChargeCheckoutStatus(payment.chargeId)
        .then((s) => {
          if (alive && s.paid) {
            setPaid(true);
            setDone(true);
          }
        })
        .catch(() => undefined);
    }, 5_000);
    return () => {
      alive = false;
      window.clearInterval(timer);
    };
  }, [payment, paid]);

  useEffect(() => {
    if (!token) {
      setLoadError('Link inválido. Volte ao sistema e tente de novo.');
      return;
    }
    let alive = true;
    fetchPlanChangePreview(token)
      .then((p) => {
        if (alive) setPreview(p);
      })
      .catch((err: unknown) => {
        if (!alive) return;
        // Cliente SEM assinatura gerenciada aqui (trial nativo do SaaS, cortesia,
        // espelho, nunca comprou) não tem o que "trocar" — pra ele isso é uma
        // COMPRA. Desviar evita o beco sem saída que o guard do checkout criava.
        const errorBody = (
          err as {
            response?: {
              data?: {
                error?: {
                  code?: string;
                  message?: string;
                  details?: {
                    charge_id?: string | null;
                    payment_url?: string | null;
                    charge_status?: string | null;
                    ends_at?: string | null;
                  };
                };
              };
            };
          }
        )?.response?.data?.error;
        const code = errorBody?.code;

        // Assinatura suspensa por falta de pagamento: não é compra nem troca —
        // é quitar o que está em aberto. Pagando, o acesso volta sozinho.
        if (code === 'SUBSCRIPTION_SUSPENDED') {
          const d = errorBody?.details;
          // Nossa página de fatura primeiro (20/10): ela aceita aguardando,
          // enviada E vencida (public-charge-checkout.service, REEMITTABLE_STATUSES)
          // e mantém o cliente no visual do checkout. A fatura do Asaas só sobra
          // pra quando não há cobrança pagável localizada.
          const payableHere =
            Boolean(d?.charge_id) &&
            ['aguardando_pagamento', 'enviada', 'vencida'].includes(d?.charge_status ?? '');
          setOverdueUrl(payableHere ? `/checkout/cobranca/${d?.charge_id}` : (d?.payment_url ?? null));
          return;
        }

        // Cancelamento agendado, ainda com acesso (27/10): NÃO desvia pra compra
        // — lá virava uma 2ª assinatura cobrada em cima do período já pago.
        if (code === 'SUBSCRIPTION_PENDING_CANCELLATION') {
          setPendingCancellation({ endsAt: errorBody?.details?.ends_at ?? null });
          return;
        }

        // Assinatura anual no meio do período (27/10): a troca automática é só
        // mensal. Sem este ramo caía no "link expirou", que manda tentar de novo
        // algo que nunca vai funcionar.
        if (code === 'PLAN_CHANGE_NEEDS_SUPPORT') {
          setLoadError(
            errorBody?.message ??
              'Pra trocar de plano no meio do período, fale com o suporte.',
          );
          return;
        }

        const fallbackable = code === 'NO_ACTIVE_SUBSCRIPTION' || code === 'CLIENT_NOT_LINKED';
        if (fallbackable && wantedPlan) {
          saveReserveiCheckoutIntent({
            handoffToken: token,
            planSlug: wantedPlan,
            cycle: params.get('cycle') ?? savedIntent?.cycle,
            redirect: params.get('redirect') ?? savedIntent?.redirect,
          });
          const fallbackParams = new URLSearchParams({ saas: saasOrigin });
          const cycle = params.get('cycle') ?? savedIntent?.cycle;
          const redirect = params.get('redirect') ?? savedIntent?.redirect;
          if (cycle) fallbackParams.set('cycle', cycle);
          if (redirect) fallbackParams.set('redirect', redirect);
          window.location.replace(
            `/checkout/${encodeURIComponent(wantedPlan)}?${fallbackParams.toString()}`,
          );
          return;
        }
        setLoadError(
          fallbackable
            ? 'Você ainda não tem uma assinatura ativa aqui. Volte ao sistema e escolha um plano.'
            : 'Este link expirou ou é inválido. Volte ao sistema e tente de novo.',
        );
      });
    return () => {
      alive = false;
    };
    // Intencional (aviso anterior a 20/10): a prévia é buscada UMA vez por
    // passe/plano/origem. `params`/`savedIntent` só alimentam o desvio pra compra
    // e mudam de identidade a cada render — incluí-los rebuscaria a prévia sem motivo.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, wantedPlan, saasOrigin, previewRound]);

  /** "Manter assinatura": desfaz o agendamento e recarrega a prévia normal. */
  async function handleKeep(): Promise<void> {
    if (keeping) return;
    setKeeping(true);
    setKeepError(null);
    try {
      await keepPlanChangeSubscription(token);
      setPendingCancellation(undefined);
      setPreviewRound((n) => n + 1);
    } catch {
      setKeepError('Não deu para manter agora. Tente de novo em instantes.');
    } finally {
      setKeeping(false);
    }
  }

  async function handleConfirm(): Promise<void> {
    if (!selected) return;
    setStarting(true);
    const chosen: Method = method ?? 'pix';
    try {
      // Cartão vai junto: o backend cobra na criação do payment. Antes a tela
      // não mandava o cartão e jogava o cliente pra fatura do Asaas — outro
      // visual, no meio do nosso checkout, na hora de digitar o cartão.
      const result = await startPlanChange({
        token,
        to_plan_id: selected.plan_id,
        payment_method: chosen,
        ...(chosen === 'credit_card'
          ? {
              credit_card: {
                number: onlyDigits(card.number),
                holder_name: card.holder_name.trim(),
                expiry_month: card.expiry_month,
                expiry_year: card.expiry_year,
                ccv: card.ccv,
                postal_code: onlyDigits(card.postal_code) || undefined,
                address_number: card.address_number.trim() || undefined,
              },
            }
          : {}),
      });
      if (result.scheduled_for) {
        setScheduledFor(result.scheduled_for);
        setDone(true);
        return;
      }
      if (result.applied) {
        setDone(true);
        return;
      }
      if (result.charge_id) {
        // O meio que vale é o da COBRANÇA: se já havia uma troca pendente pro
        // mesmo plano, o backend devolve a cobrança existente (com o meio dela)
        // e ignora o cartão digitado agora.
        const effective: Method =
          result.payment_method === 'pix' || result.payment_method === 'credit_card'
            ? result.payment_method
            : chosen;
        // Pix: o QR sai do checkout de cobrança (mesma charge, mesmo payment —
        // só lê o QR, não emite nada novo).
        const pix =
          effective === 'pix'
            ? await payCharge(result.charge_id, { payment_method: 'pix' }).catch(() => null)
            : null;
        setPayment({
          chargeId: result.charge_id,
          method: effective,
          pix,
          fallbackUrl: result.payment_url,
        });
        return;
      }
      if (result.payment_url) {
        window.location.href = result.payment_url;
        return;
      }
      toast.error('Não conseguimos abrir o pagamento. Tente de novo.');
    } catch (err: unknown) {
      // Sessão vencida no meio da decisão: o cliente NÃO fez nada errado e não
      // pode levar "erro genérico" — precisa saber que é só refazer o caminho.
      const code = (err as { response?: { data?: { error?: { code?: string } } } })?.response?.data
        ?.error?.code;
      if (code === 'TOKEN_EXPIRED' || code === 'TOKEN_INVALID') {
        setLoadError(
          'Esta página ficou aberta tempo demais e a sessão expirou. Volte ao sistema e clique em trocar de plano de novo — nada foi cobrado.',
        );
        return;
      }
      // O link da troca é de uso único (consumido antes da cobrança), então não
      // dá pra só "tentar de novo" aqui — o cliente refaz o caminho pelo sistema.
      if (code === 'ASAAS_PAYMENT_FAILED' && chosen === 'credit_card') {
        setLoadError(
          'O cartão não foi aprovado e nada foi cobrado. Volte ao sistema, clique em trocar de plano de novo e confira os dados ou use outro cartão.',
        );
        return;
      }
      if (code === 'TOKEN_ALREADY_USED') {
        setLoadError(
          'Essa troca já foi iniciada. Volte ao sistema pra ver como ficou o seu plano.',
        );
        return;
      }
      toast.error('Não conseguimos iniciar a troca. Tente de novo.');
    } finally {
      setStarting(false);
    }
  }

  // Reservei: MESMO fluxo acima — passe, prévia, cobrança, polling, volta — no
  // visual do checkout novo (2026-10-20). Decide por `saas=reservei` no link OU
  // pelo emissor do próprio passe (`iss`). NÃO por "tem passe salvo": ele é
  // gravado pra QUALQUER emissor (useLayoutEffect acima) e os links dos outros
  // SaaS vêm só com `?token=` — o cliente do OwlTask veria a marca do Reservei.
  // Outros emissores seguem no visual padrão abaixo.
  if (params.get('saas') === 'reservei' || readHandoffIssuer(token) === 'reservei') {
    return (
      <ReserveiPlanChange
        preview={preview}
        loadError={loadError}
        overdueUrl={overdueUrl}
        pendingCancellation={pendingCancellation}
        keeping={keeping}
        keepError={keepError}
        onKeep={() => void handleKeep()}
        selected={selected}
        onSelect={setSelected}
        method={method}
        onMethod={setMethod}
        card={card}
        onCard={setCard}
        starting={starting}
        done={done}
        scheduledFor={scheduledFor}
        payment={payment}
        paid={paid}
        backUrl={backUrl}
        autoReturn={autoReturn}
        returnIn={returnIn}
        onConfirm={() => void handleConfirm()}
      />
    );
  }

  // ---------------------------------------------------------------- estados
  if (pendingCancellation) {
    return (
      <CheckoutShell title="CANCELAMENTO AGENDADO">
        <div className="mx-auto max-w-lg rounded-[16px] border border-black/[0.08] bg-white p-8 text-center shadow-[0_1px_2px_rgba(0,0,0,0.05)]">
          <h2 className="mb-3 text-[20px] font-black tracking-tight text-black">
            Seu cancelamento está agendado
          </h2>
          <p className="text-[14px] leading-relaxed text-black/60">
            {pendingCancellation.endsAt
              ? `Seu acesso continua até ${formatDay(pendingCancellation.endsAt)}. `
              : ''}
            Pra trocar de plano, primeiro mantenha a assinatura — a cobrança volta a
            seguir o ciclo de sempre.
          </p>
          <button
            type="button"
            onClick={() => void handleKeep()}
            disabled={keeping}
            className="mt-7 inline-flex h-12 items-center justify-center gap-2 rounded-[10px] bg-gradient-to-r from-teal-600 to-teal-500 px-6 text-[12px] font-bold uppercase tracking-[0.2em] text-white transition-all hover:from-teal-700 hover:to-teal-600 disabled:opacity-60"
          >
            {keeping ? 'Mantendo…' : 'Manter assinatura'}
          </button>
          {keepError && (
            <p role="alert" className="mt-4 text-[13px] text-red-600">
              {keepError}
            </p>
          )}
          {backUrl && (
            <a href={backUrl} className="mt-5 block text-[13px] text-black/60 underline">
              Voltar ao sistema
            </a>
          )}
        </div>
      </CheckoutShell>
    );
  }

  if (overdueUrl !== undefined) {
    return (
      <CheckoutShell title="PAGAMENTO PENDENTE">
        <div className="mx-auto max-w-lg rounded-[16px] border border-black/[0.08] bg-white p-8 text-center shadow-[0_1px_2px_rgba(0,0,0,0.05)]">
          <div className="mx-auto mb-5 flex h-12 w-12 items-center justify-center rounded-full bg-amber-500/15">
            <Calendar className="h-6 w-6 text-amber-700" strokeWidth={2} />
          </div>
          <h2 className="mb-3 text-[20px] font-black tracking-tight text-black">
            Sua assinatura está suspensa
          </h2>
          <p className="text-[14px] leading-relaxed text-black/60">
            Ficou uma cobrança em aberto, então o acesso foi pausado. Assim que o pagamento for
            confirmado, seu plano volta automaticamente — e aí você consegue trocar de plano
            normalmente.
          </p>
          {overdueUrl ? (
            <a
              href={overdueUrl}
              style={{ color: '#09090b' }}
              className="mt-7 inline-flex h-12 items-center justify-center gap-2 rounded-[10px] bg-gradient-to-r from-teal-600 to-teal-500 px-6 text-[12px] font-bold uppercase tracking-[0.2em] text-white transition-all hover:from-teal-700 hover:to-teal-600"
            >
              Pagar agora
              <ArrowRight className="h-4 w-4" strokeWidth={2.5} />
            </a>
          ) : (
            <p className="mt-6 text-[13px] text-black/50">
              Fale com a gente pra receber a segunda via da cobrança.
            </p>
          )}
          {backUrl && (
            <a href={backUrl} className="mt-5 block text-[13px] text-black/60 underline">
              Voltar ao sistema
            </a>
          )}
        </div>
      </CheckoutShell>
    );
  }

  if (loadError) {
    return (
      <CheckoutShell title="TROCAR PLANO">
        <div className="mx-auto max-w-md rounded-[16px] border border-black/[0.08] bg-white p-8 text-center">
          <p className="text-[14px] leading-relaxed text-black/70">{loadError}</p>
          {backUrl && (
            <a
              href={backUrl}
              className="mt-6 inline-flex h-12 items-center justify-center rounded-[10px] bg-black px-6 text-[12px] font-bold uppercase tracking-[0.2em] text-white transition-colors hover:bg-black/85"
            >
              Voltar ao sistema
            </a>
          )}
        </div>
      </CheckoutShell>
    );
  }

  if (!preview) {
    return (
      <CheckoutShell title="TROCAR PLANO">
        <div className="grid grid-cols-1 items-start gap-10 lg:grid-cols-[minmax(0,1fr)_420px] lg:gap-16">
          <div className="space-y-3">
            {[0, 1, 2].map((i) => (
              <div key={i} className="h-[92px] animate-pulse rounded-[12px] bg-black/[0.05]" />
            ))}
          </div>
          <div className="h-[320px] animate-pulse rounded-[16px] bg-black/[0.05]" />
        </div>
      </CheckoutShell>
    );
  }

  if (done) {
    return (
      <CheckoutShell title={scheduledFor ? 'AGENDADO' : 'PRONTO'}>
        <div className="mx-auto max-w-lg rounded-[16px] border border-black/[0.08] bg-white p-10 text-center shadow-[0_1px_2px_rgba(0,0,0,0.05)]">
          <div className="mx-auto mb-5 flex h-12 w-12 items-center justify-center rounded-full bg-teal-600">
            <Check className="h-6 w-6 text-white" strokeWidth={3} />
          </div>
          <h2 className="mb-3 text-[24px] font-black tracking-tight text-black">
            {scheduledFor ? 'Mudança agendada' : `Você está no ${selected?.name}`}
          </h2>
          <p className="text-[14px] leading-relaxed text-black/60">
            {scheduledFor ? (
              <>
                Você continua no <strong className="text-black">{preview.current_plan.name}</strong>{' '}
                até <strong className="text-black">{formatDay(scheduledFor)}</strong> — esse período
                já está pago. A partir dessa data você passa pro {selected?.name} e a mensalidade
                cai pra {formatBRL(selected?.monthly_price ?? '0')}. Nada foi cobrado agora.
              </>
            ) : paid ? (
              <>
                Recebemos seu pagamento e o plano novo já está sendo liberado. A próxima cobrança
                sai no valor do {selected?.name}.
              </>
            ) : (
              <>
                O plano novo já está liberado. Nada foi cobrado agora — a próxima cobrança sai no
                valor do {selected?.name}, na data que já estava prevista.
              </>
            )}
          </p>
          {backUrl && (
            <a
              href={backUrl}
              className="mt-8 inline-flex h-12 items-center justify-center gap-2 rounded-[10px] bg-black px-6 text-[12px] font-bold uppercase tracking-[0.2em] text-white transition-colors hover:bg-black/85"
            >
              Voltar ao sistema
            </a>
          )}
          {autoReturn && (
            <p className="mt-4 text-[12px] text-black/45">
              Voltando pro sistema em {returnIn}s…
            </p>
          )}
        </div>
      </CheckoutShell>
    );
  }

  if (payment) {
    return (
      <CheckoutShell title="PAGAMENTO">
        <div className="mx-auto max-w-lg">
          {payment.method === 'pix' ? (
            payment.pix?.pix_qr_image && payment.pix.pix_payload ? (
              <CheckoutSection title="Pague com Pix" icon={QrCode}>
                <div className="flex justify-center">
                  <div className="rounded-[12px] border border-black/10 bg-white p-3">
                    <img
                      src={`data:image/png;base64,${payment.pix.pix_qr_image}`}
                      alt="QR Code Pix"
                      className="h-[220px] w-[220px]"
                    />
                  </div>
                </div>
                <div className="mt-5">
                  <CopyCodeField
                    code={payment.pix.pix_payload}
                    label="Ou copie o código"
                    successMessage="Código Pix copiado!"
                  />
                </div>
                <CheckoutWaitingFooter>
                  Aguardando o Pix — o plano {selected?.name} libera assim que confirmar…
                </CheckoutWaitingFooter>
              </CheckoutSection>
            ) : (
              <CheckoutErrorBox
                message="Não conseguimos gerar o QR do Pix agora. Use o link do Asaas."
                fallbackUrl={payment.fallbackUrl}
              />
            )
          ) : (
            <CheckoutSection title="Processando cartão…" icon={CreditCard}>
              <p className="text-[13px] text-black/55">
                Enviamos a cobrança pra autorização do banco. Em geral é instantâneo — assim que
                confirmar, esta tela atualiza e o plano {selected?.name} é liberado.
              </p>
              <CheckoutWaitingFooter />
            </CheckoutSection>
          )}
        </div>
      </CheckoutShell>
    );
  }

  if (preview.eligible_upgrades.length === 0) {
    return (
      <CheckoutShell title="TROCAR PLANO">
        <div className="mx-auto max-w-md rounded-[16px] border border-black/[0.08] bg-white p-8 text-center">
          <h2 className="mb-2 text-[18px] font-black text-black">Nenhum plano pra trocar</h2>
          <p className="text-[13px] text-black/60">
            Seu plano atual é o {preview.current_plan.name} e não há outro plano disponível neste
            serviço no momento.
          </p>
          {backUrl && (
            <a href={backUrl} className="mt-6 inline-block text-[13px] text-black underline">
              Voltar ao sistema
            </a>
          )}
        </div>
      </CheckoutShell>
    );
  }

  // ---------------------------------------------------------------- principal
  /** Modo renewal: fatura de renovação aberta — trocar SUBSTITUI a fatura
   *  (mensalidade cheia do plano novo, com desconto vigente), sem pro-rata e
   *  sem agendamento. Suspenso/1ª fatura: pagar (re)ativa na hora. */
  const { isRenewal, isDowngrade, renewalAmount, renewalHasDiscount, isFree, needsPayment, daysLeft } =
    planChangeMath(preview, selected);
  const cardValid =
    onlyDigits(card.number).length >= 13 &&
    card.holder_name.trim().length >= 2 &&
    /^(0[1-9]|1[0-2])$/.test(card.expiry_month) &&
    /^\d{4}$/.test(card.expiry_year) &&
    card.ccv.length >= 3;
  const ctaDisabled =
    starting ||
    !selected ||
    (needsPayment && (!method || (method === 'credit_card' && !cardValid)));

  return (
    <CheckoutShell title="TROCAR PLANO">

      <div className="grid grid-cols-1 items-start gap-10 lg:grid-cols-[minmax(0,1fr)_420px] lg:gap-16">
        {/* ============ COLUNA ESQUERDA — escolha ============ */}
        <div>
          {isRenewal && (
            <div className="mb-10 flex items-start gap-3 rounded-[10px] border border-teal-700/25 bg-teal-50 p-4">
              <Calendar className="mt-0.5 h-4 w-4 shrink-0 text-teal-800" strokeWidth={2} />
              <p className="text-[12px] leading-relaxed text-teal-950">
                {preview.suspended ? (
                  <>
                    Sua assinatura está <strong>pausada por falta de pagamento</strong> e a
                    renovação está em aberto. Escolha qualquer plano abaixo — ao pagar, seu acesso
                    volta na hora, já no plano escolhido.
                  </>
                ) : preview.first_purchase ? (
                  <>
                    Sua primeira mensalidade ainda está em aberto. Se preferir <strong>começar em
                    outro plano</strong>, escolha abaixo — a fatura antiga é cancelada e você ativa
                    direto no plano novo.
                  </>
                ) : (
                  <>
                    Sua <strong>renovação está em aberto</strong>. Você pode renovar no seu plano
                    atual ou já renovar em outro plano — a fatura antiga é substituída pela nova.
                  </>
                )}
              </p>
            </div>
          )}

          <div className="mb-12">
            <CheckoutSectionTitle>Plano atual</CheckoutSectionTitle>
            <div className="flex items-end justify-between py-1">
              <div>
                <p className="mb-1 text-[13px] uppercase tracking-wider text-black/40">
                  {isRenewal && preview.suspended ? 'Assinatura pausada' : 'Assinatura ativa'}
                </p>
                <p className="text-[28px] font-black leading-none tracking-tight text-black">
                  {preview.current_plan.name}
                </p>
              </div>
              <p className="text-[15px] font-bold tabular-nums text-black/70">
                {formatBRL(preview.current_plan.monthly_price)}
                <span className="text-[12px] font-normal text-black/40">{cycleSuffix(preview)}</span>
              </p>
            </div>
            {isRenewal && preview.current_plan_payment_url && (
              <a
                href={preview.current_plan_payment_url}
                className="mt-4 inline-flex h-11 items-center justify-center gap-2 rounded-[10px] border border-black/15 bg-white px-5 text-[12px] font-bold uppercase tracking-[0.15em] text-black transition-colors hover:border-black/40"
              >
                Pagar minha renovação no {preview.current_plan.name}
                <ArrowRight className="h-4 w-4" strokeWidth={2.5} />
              </a>
            )}
          </div>

          {preview.pending_downgrade && (
            <div className="mb-10 flex items-start gap-3 rounded-[10px] border border-amber-600/25 bg-amber-50 p-4">
              <Calendar className="mt-0.5 h-4 w-4 shrink-0 text-amber-700" strokeWidth={2} />
              <p className="text-[12px] leading-relaxed text-amber-900">
                Você já tem uma mudança agendada para{' '}
                <strong>{formatDay(preview.pending_downgrade.scheduled_for)}</strong>. Escolher
                outro plano agora substitui esse agendamento.
              </p>
            </div>
          )}

          <div className="mb-12">
            <CheckoutSectionTitle>Escolha o novo plano</CheckoutSectionTitle>
            <div className="flex flex-col gap-3">
              {preview.eligible_upgrades.map((up) => {
                const active = selected?.plan_id === up.plan_id;
                const up_ = up.direction === 'upgrade';
                return (
                  <button
                    key={up.plan_id}
                    type="button"
                    onClick={() => setSelected(up)}
                    aria-pressed={active}
                    className={`group flex items-center gap-4 rounded-[12px] border bg-white p-5 text-left transition-all duration-200 ease-[cubic-bezier(0.19,1,0.22,1)] ${
                      active
                        ? 'border-black shadow-[0_4px_12px_rgba(0,0,0,0.08)]'
                        : 'border-black/[0.10] hover:border-black/30 hover:shadow-[0_2px_8px_rgba(0,0,0,0.05)]'
                    }`}
                  >
                    <span
                      aria-hidden="true"
                      className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border transition-colors ${
                        active ? 'border-black bg-black' : 'border-black/25'
                      }`}
                    >
                      {active && <Check className="h-3 w-3 text-white" strokeWidth={3} />}
                    </span>

                    <span className="min-w-0 flex-1">
                      <span className="flex items-center gap-2">
                        <span className="text-[16px] font-bold text-black">{up.name}</span>
                        {/* No renewal não existe "diferença agora" nem agendamento —
                            o badge de direção só confunde. */}
                        {!isRenewal && (
                          <span
                            className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${
                              up_ ? 'bg-teal-600/10 text-teal-800' : 'bg-black/[0.06] text-black/60'
                            }`}
                          >
                            {up_ ? (
                              <TrendingUp className="h-3 w-3" strokeWidth={2.5} />
                            ) : (
                              <TrendingDown className="h-3 w-3" strokeWidth={2.5} />
                            )}
                            {up_ ? 'Upgrade' : 'Downgrade'}
                          </span>
                        )}
                      </span>
                      <span className="mt-1 block text-[12px] text-black/50">
                        {formatBRL(up.monthly_price)}/mês
                      </span>
                    </span>

                    <span className="shrink-0 text-right">
                      {isRenewal ? (
                        <>
                          <span className="block text-[10px] uppercase tracking-wider text-black/40">
                            Renove por
                          </span>
                          <span className="block text-[19px] font-black tabular-nums text-black">
                            {formatBRL(up.effective_renewal_price ?? up.renewal_price ?? up.monthly_price)}
                          </span>
                          {up.renewal_price &&
                          up.effective_renewal_price &&
                          up.renewal_price !== up.effective_renewal_price ? (
                            <span className="block text-[10px] text-black/40">
                              <s>{formatBRL(up.renewal_price)}</s> com seu desconto
                            </span>
                          ) : (
                            <span className="block text-[10px] text-black/40">
                              mensalidade do mês
                            </span>
                          )}
                        </>
                      ) : up_ ? (
                        <>
                          <span className="block text-[10px] uppercase tracking-wider text-black/40">
                            Você paga agora
                          </span>
                          <span className="block text-[19px] font-black tabular-nums text-black">
                            {formatBRL(up.proration_amount)}
                          </span>
                          <span className="block text-[10px] text-black/40">
                            {Number(up.proration_amount) > 0
                              ? `proporcional aos ${up.days_remaining} ${plural(up.days_remaining, 'dia restante', 'dias restantes')}`
                              : 'sem cobrança (em teste)'}
                          </span>
                        </>
                      ) : (
                        <>
                          <span className="block text-[10px] uppercase tracking-wider text-black/40">
                            Sem cobrança agora
                          </span>
                          <span className="block text-[15px] font-bold text-black">
                            a partir de {formatDay(up.scheduled_for)}
                          </span>
                          {up.monthly_savings && (
                            <span className="block text-[10px] text-black/40">
                              economiza {formatBRL(up.monthly_savings)}/mês
                            </span>
                          )}
                        </>
                      )}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {needsPayment && (
            <div className="mb-2">
              <CheckoutSectionTitle>Forma de pagamento</CheckoutSectionTitle>
              {/* Só Pix e cartão — boleto fica fora de propósito: numa troca de
                  plano o acesso novo só libera quando o pagamento confirma, e
                  esperar 1-3 dias úteis por compensação trava o cliente. */}
              <div className="flex flex-col gap-3">
                {PAYMENT_METHODS.map(({ id, label, hint, badge }) => {
                  const active = method === id;
                  return (
                    <button
                      key={id}
                      type="button"
                      onClick={() => setMethod(id)}
                      aria-pressed={active}
                      className={`flex items-center gap-4 rounded-[12px] border bg-white p-4 text-left transition-all duration-200 ease-[cubic-bezier(0.19,1,0.22,1)] ${
                        active
                          ? 'border-teal-600 shadow-[0_4px_12px_rgba(0,0,0,0.08)]'
                          : 'border-black/[0.10] hover:border-black/30'
                      }`}
                    >
                      <span
                        aria-hidden="true"
                        className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border transition-colors ${
                          active ? 'border-teal-600 bg-teal-600' : 'border-black/25'
                        }`}
                      >
                        {active && <Check className="h-3 w-3 text-white" strokeWidth={3} />}
                      </span>

                      <span className="min-w-0 flex-1">
                        <span className="flex flex-wrap items-center gap-2">
                          <span className="text-[14px] font-bold uppercase tracking-wide text-black">
                            {label}
                          </span>
                          {badge && (
                            <span className="rounded-full bg-teal-600/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-teal-800">
                              {badge}
                            </span>
                          )}
                        </span>
                        <span className="mt-0.5 block text-[11px] text-black/45">{hint}</span>
                      </span>

                      <span className="flex shrink-0 items-center gap-2">
                        {id === 'pix' ? (
                          <PixIcon size={28} aria-label="" />
                        ) : (
                          <>
                            <VisaIcon size={26} aria-label="" />
                            <MasterCardIcon size={26} aria-label="" />
                            <EloIcon size={26} aria-label="" />
                          </>
                        )}
                      </span>
                    </button>
                  );
                })}
              </div>

              {method === 'credit_card' && (
                <div className="mt-5 flex flex-col gap-3 rounded-[12px] border border-black/[0.10] bg-white p-5">
                  <CardInput
                    label="Número do cartão"
                    value={card.number}
                    onChange={(v) => setCard((c) => ({ ...c, number: maskCard(v) }))}
                    placeholder="1234 5678 9012 3456"
                  />
                  <CardInput
                    label="Nome impresso no cartão"
                    value={card.holder_name}
                    onChange={(v) => setCard((c) => ({ ...c, holder_name: v }))}
                    placeholder="JOÃO DA SILVA"
                  />
                  <div className="grid grid-cols-3 gap-3">
                    <CardInput
                      label="Mês"
                      value={card.expiry_month}
                      onChange={(v) =>
                        setCard((c) => ({ ...c, expiry_month: onlyDigits(v).slice(0, 2) }))
                      }
                      placeholder="MM"
                    />
                    <CardInput
                      label="Ano"
                      value={card.expiry_year}
                      onChange={(v) =>
                        setCard((c) => ({ ...c, expiry_year: onlyDigits(v).slice(0, 4) }))
                      }
                      placeholder="AAAA"
                    />
                    <CardInput
                      label="CVV"
                      value={card.ccv}
                      onChange={(v) => setCard((c) => ({ ...c, ccv: onlyDigits(v).slice(0, 4) }))}
                      placeholder="123"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <CardInput
                      label="CEP do titular"
                      value={card.postal_code}
                      onChange={(v) => setCard((c) => ({ ...c, postal_code: maskCep(v) }))}
                      placeholder="00000-000"
                    />
                    <CardInput
                      label="Nº do endereço"
                      value={card.address_number}
                      onChange={(v) => setCard((c) => ({ ...c, address_number: v }))}
                      placeholder="100"
                    />
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* ============ COLUNA DIREITA — resumo ============ */}
        <div className="lg:sticky lg:top-12">
          <div className="rounded-[16px] border border-black/[0.08] bg-white p-7 shadow-[0_1px_2px_rgba(0,0,0,0.05)]">
            <div className="mb-1 flex items-center justify-between">
              <h2 className="text-[11px] font-bold uppercase tracking-[0.2em] text-black">
                Resumo
              </h2>
              <span className="text-[11px] uppercase tracking-wider text-black/40">
                {selected ? 'Mudança de plano' : 'Escolha um plano'}
              </span>
            </div>
            <div className="mb-6 h-px bg-black" />

            {!selected ? (
              <p className="py-8 text-center text-[13px] text-black/40">
                Selecione um plano ao lado pra ver os valores.
              </p>
            ) : (
              <>
                <div className="flex items-start gap-4 border-b border-black/[0.08] pb-6">
                  <div className="relative flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-[10px] border border-black/[0.12] bg-gradient-to-br from-[#F5F1EC] to-[#EBE5DC]">
                    <div
                      aria-hidden="true"
                      className="absolute inset-0 opacity-[0.04]"
                      style={{
                        backgroundImage:
                          'repeating-linear-gradient(45deg, #000 0, #000 1px, transparent 1px, transparent 8px)',
                      }}
                    />
                    <span
                      className="relative z-10 bg-gradient-to-br from-teal-700 to-teal-900 bg-clip-text text-[42px] font-black leading-none tracking-tighter text-transparent"
                      style={{
                        fontFamily: "'Playfair Display', Georgia, serif",
                        fontStyle: 'italic',
                      }}
                    >
                      {selected.name.charAt(0)}
                    </span>
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-[15px] font-bold leading-tight text-black">
                      Plano {selected.name}
                    </p>
                    <div className="mt-2 space-y-1">
                      <p className="text-[11px] uppercase tracking-wider text-black/50">
                        <span className="text-black/40">De:</span>{' '}
                        <span className="font-medium text-black/70">
                          {preview.current_plan.name}
                        </span>
                      </p>
                      <p className="text-[11px] uppercase tracking-wider text-black/50">
                        <span className="text-black/40">Mensalidade:</span>{' '}
                        <span className="font-medium text-black/70">
                          {formatBRL(selected.monthly_price)}
                        </span>
                      </p>
                    </div>
                  </div>
                </div>

                {/* Breakdown — a conta explicada */}
                <div className="space-y-3 border-b border-black/[0.08] py-6">
                  {isRenewal ? (
                    <>
                      <div className="flex items-center justify-between">
                        <span className="text-[12px] uppercase tracking-wider text-black/50">
                          Mensalidade do {selected.name}
                        </span>
                        <span className="text-[13px] font-medium tabular-nums text-black/70">
                          {formatBRL(selected.renewal_price ?? selected.monthly_price)}
                        </span>
                      </div>
                      {renewalHasDiscount && (
                        <div className="flex items-center justify-between">
                          <span className="text-[12px] uppercase tracking-wider text-black/50">
                            Seu desconto vigente
                          </span>
                          <span className="text-[13px] font-medium tabular-nums text-teal-800">
                            −{' '}
                            {formatBRL(
                              String(
                                Number(selected.renewal_price ?? '0') -
                                  Number(selected.effective_renewal_price ?? '0'),
                              ),
                            )}
                          </span>
                        </div>
                      )}
                      <div className="flex items-center justify-between">
                        <span className="text-[12px] uppercase tracking-wider text-black/50">
                          Fatura anterior
                        </span>
                        <span className="text-[13px] font-medium tabular-nums text-black/70">
                          Cancelada — substituída por esta
                        </span>
                      </div>
                    </>
                  ) : isDowngrade ? (
                    <>
                      <div className="flex items-center justify-between">
                        <span className="text-[12px] uppercase tracking-wider text-black/50">
                          Cobrança agora
                        </span>
                        <span className="text-[13px] font-medium tabular-nums text-black/70">
                          Nenhuma
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-[12px] uppercase tracking-wider text-black/50">
                          Passa a valer em
                        </span>
                        <span className="text-[13px] font-medium tabular-nums text-black/70">
                          {formatDay(selected.scheduled_for)}
                        </span>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="flex items-center justify-between">
                        <span className="text-[12px] uppercase tracking-wider text-black/50">
                          Diferença mensal
                        </span>
                        <span className="text-[13px] font-medium tabular-nums text-black/70">
                          {formatBRL(
                            String(
                              Number(selected.monthly_price) -
                                Number(preview.current_plan.monthly_price),
                            ),
                          )}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-[12px] uppercase tracking-wider text-black/50">
                          Dias restantes do seu plano
                        </span>
                        <span className="text-[13px] font-medium tabular-nums text-black/70">
                          {daysLeft} {plural(daysLeft, 'dia', 'dias')}
                        </span>
                      </div>
                    </>
                  )}
                </div>

                <div className="flex items-end justify-between py-6">
                  <span className="text-[13px] font-bold uppercase tracking-[0.15em] text-black">
                    Total agora
                  </span>
                  <span className="text-[30px] font-black leading-none tabular-nums text-black">
                    {isRenewal
                      ? formatBRL(renewalAmount)
                      : isDowngrade
                        ? formatBRL('0')
                        : formatBRL(selected.proration_amount)}
                  </span>
                </div>

                {/* A EXPLICAÇÃO — por que esse valor e não o preço cheio */}
                <div className="rounded-[10px] bg-[#F5F1EC] p-4">
                  <p className="text-[12px] leading-relaxed text-black/70">
                    {isRenewal ? (
                      <>
                        Esta é a <strong className="text-black">renovação da sua assinatura, já
                        no {selected.name}</strong>. A fatura anterior
                        {preview.suspended ? ' (que estava em aberto)' : ''} é cancelada e
                        substituída por esta.{' '}
                        {preview.suspended
                          ? 'Assim que o pagamento confirmar, seu acesso volta na hora, direto no plano novo.'
                          : preview.first_purchase
                            ? 'Assim que o pagamento confirmar, sua conta ativa direto no plano escolhido.'
                            : 'Assim que o pagamento confirmar, o plano novo vale na hora.'}
                      </>
                    ) : isDowngrade ? (
                      <>
                        Você <strong className="text-black">não paga nada agora</strong> e continua
                        no {preview.current_plan.name} até {formatDay(selected.scheduled_for)} —
                        esse período já está pago. A partir daí a mensalidade cai pra{' '}
                        {formatBRL(selected.monthly_price)}.
                      </>
                    ) : isFree ? (
                      <>
                        Você está em período de teste, então{' '}
                        <strong className="text-black">não há nada a pagar agora</strong>. O plano
                        novo vale na hora e a primeira cobrança sai no valor do {selected.name}, na
                        data já prevista.
                      </>
                    ) : (
                      <>
                        Você não paga o valor cheio: paga{' '}
                        <strong className="text-black">
                          só a diferença proporcional aos {daysLeft}{' '}
                          {plural(daysLeft, 'dia que falta', 'dias que faltam')}
                        </strong>{' '}
                        do seu ciclo atual. O valor integral de {formatBRL(selected.monthly_price)}{' '}
                        só passa a valer na próxima cobrança.
                      </>
                    )}
                  </p>
                </div>
              </>
            )}

            <button
              type="button"
              onClick={() => void handleConfirm()}
              disabled={ctaDisabled}
              className={`mt-6 flex h-14 w-full items-center justify-center gap-3 rounded-[10px] text-[13px] font-bold uppercase tracking-[0.2em] transition-all duration-200 ease-[cubic-bezier(0.19,1,0.22,1)] ${
                ctaDisabled
                  ? 'cursor-not-allowed bg-black/[0.08] text-black/30'
                  : 'cursor-pointer bg-gradient-to-r from-teal-600 to-teal-500 text-white shadow-[0_4px_12px_rgba(0,0,0,0.08)] hover:from-teal-700 hover:to-teal-600 hover:shadow-[0_8px_24px_rgba(0,0,0,0.12)]'
              }`}
            >
              {starting
                ? 'Processando…'
                : !selected
                  ? 'Escolha um plano'
                  : isDowngrade
                    ? 'Agendar mudança'
                    : isFree
                      ? 'Mudar agora'
                      : 'Continuar para pagamento'}
              {!ctaDisabled && <ArrowRight className="h-4 w-4" strokeWidth={2.5} />}
            </button>

            <div className="mt-5 space-y-2 border-t border-black/[0.08] pt-5">
              {[
                isDowngrade
                  ? 'Sem multa nem taxa de mudança'
                  : 'Acesso liberado após a confirmação',
                'Cancele quando quiser',
                'Pagamento seguro via Asaas',
              ].map((t) => (
                <div key={t} className="flex items-center gap-2">
                  <Check className="h-3.5 w-3.5 shrink-0 text-teal-600" strokeWidth={3} />
                  <span className="text-[11px] uppercase tracking-wider text-black/50">{t}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </CheckoutShell>
  );
}
